import React, { useState, useEffect, useCallback } from 'react';
import { Container, Spinner, Alert, Form, Table, Button, Row, Col, Badge, Card } from 'react-bootstrap';
import TribeBadge from './TribeBadge';

interface EpisodeScore {
  tribeImmunityWins: number;
  tribeRewardWins: number;
  individualRewardWins: number;
  rightSideOfVote: number;
  individualImmunityWins: number;
  votesAgainst: number;
  idolObtained: number;
  advantageObtained: number;
  playedIdolAdvantage: number;
  advantageAbandoned: number;
  idolAbandoned: number;
  shotInDarkWin: number;
  coinFlipWin?: number;
  finalPlacement: number;
  votedOut: boolean;
  involuntaryEvacuation: boolean;
  voluntaryExit?: boolean;
  notes?: string;
}

interface GeminiScoreResult {
  episodeTitle?: string;
  episodeSummary?: string;
  votedOutCastaway?: string;
  scores?: any[];
  sources?: { title?: string; uri?: string }[];
}

const AdminScoring: React.FC = () => {
  const [adminSeason, setAdminSeason] = useState<number>(() => parseInt(localStorage.getItem('adminSelectedSeason') || '50'));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeminiLoading, setIsGeminiLoading] = useState(false);
  const [geminiResult, setGeminiResult] = useState<GeminiScoreResult | null>(null);
  const [geminiNotes, setGeminiNotes] = useState<{ [castawayId: number]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [leagues, setLeagues] = useState<any[]>([]);
  const [castaways, setCastaways] = useState<any[]>([]);
  const [allScores, setAllScores] = useState<any>({});
  
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>(() => localStorage.getItem('adminSelectedLeagueId') || '');
  const [selectedEpisode, setSelectedEpisode] = useState<string>('1');

  const loadLeaguesAndCastaways = useCallback(async (season: number) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Use /api/leagues/all and filter manually to ensure we get the correct season's leagues
      // as seen in AdminCastawayManagement.tsx
      const [leaguesRes, scoresRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
        fetch(`${process.env.REACT_APP_API_URL}/api/scores/${season}`)
      ]);

      if (!leaguesRes.ok) throw new Error('Failed to fetch leagues');
      
      const leaguesData = await leaguesRes.json();
      const rawScoresData = scoresRes.ok ? await scoresRes.json() : null;

      // Normalize scores data: handles both { scores: {} } and [{ scores: {} }] or direct maps
      let scoresObj = {};
      if (Array.isArray(rawScoresData)) {
        const item = rawScoresData.find((d: any) => d.season === season) || rawScoresData[0];
        scoresObj = item?.scores || {};
      } else if (rawScoresData) {
        if (rawScoresData.scores) {
          // Handle double nesting like { scores: { "50": { "1": ... } } }
          if (rawScoresData.scores[season]) {
            scoresObj = rawScoresData.scores[season];
          } else if (rawScoresData.scores[String(season)]) {
            scoresObj = rawScoresData.scores[String(season)];
          } else {
            scoresObj = rawScoresData.scores;
          }
        } else if (Object.keys(rawScoresData).some(k => k.startsWith('episode'))) {
          scoresObj = rawScoresData;
        }
      }

      const allLeagues = leaguesData.leagues || (Array.isArray(leaguesData) ? leaguesData : []);
      const leaguesList = allLeagues.filter((l: any) => (Number(l.season) || 49) === Number(season));
      setLeagues(leaguesList);
      setAllScores(scoresObj);

      if (leaguesList.length > 0) {
        // Prioritize the league selected in the admin banner (stored in localStorage)
        const storedLid = localStorage.getItem('adminSelectedLeagueId');
        const currentLeague = leaguesList.find((l: any) => String(l.id) === String(storedLid)) || leaguesList[0];
        const lid = currentLeague.id;
        
        setSelectedLeagueId(lid);
        
        const castRes = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${lid}/castaways`);
        if (castRes.ok) {
          const data = await castRes.json();
          setCastaways(data.castaways || (Array.isArray(data) ? data : []));
        } else if (leaguesList[0].castaways) {
          setCastaways(leaguesList[0].castaways);
        }
      } else {
        setCastaways([]);
        setSelectedLeagueId('');
      }

    } catch (err) {
      console.error('AdminScoring: Error in loadLeaguesAndCastaways:', err);
      setError('Could not load data for Season ' + season);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaguesAndCastaways(adminSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '50');
      setAdminSeason(newSeason);
      setGeminiResult(null);
      setError(null);
      setSuccess(null);
      loadLeaguesAndCastaways(newSeason);
    };

    const handleLeagueChange = () => {
      const newLeagueId = localStorage.getItem('adminSelectedLeagueId') || '';
      setSelectedLeagueId(newLeagueId);
      setGeminiResult(null);
      setError(null);
      setSuccess(null);
    };

    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    window.addEventListener('adminLeagueChanged', handleLeagueChange);
    return () => {
      window.removeEventListener('adminSeasonChanged', handleSeasonChange);
      window.removeEventListener('adminLeagueChanged', handleLeagueChange);
    };
  }, [adminSeason, loadLeaguesAndCastaways]);

  // When league changes, fetch its castaways
  useEffect(() => {
    const fetchCastawaysForLeague = async () => {
      if (!selectedLeagueId) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways`);
        if (res.ok) {
          const data = await res.json();
          setCastaways(data.castaways || (Array.isArray(data) ? data : []));
        }
      } catch (e) {
        console.error("Failed to fetch castaways", e);
      }
    };
    fetchCastawaysForLeague();
  }, [selectedLeagueId]);

  const handleScoreChange = (castawayId: number, field: keyof EpisodeScore, value: any) => {
    setAllScores((prev: any) => {
      // Use existing key format ("episode1" or "1") if present, otherwise default to selectedEpisode
      const episodeKey = prev[`episode${selectedEpisode}`] ? `episode${selectedEpisode}` : selectedEpisode;
      const episodeScores = prev[episodeKey] || {};
      const castawayScores = episodeScores[castawayId] || {
        tribeImmunityWins: 0,
        tribeRewardWins: 0,
        individualRewardWins: 0,
        rightSideOfVote: 0,
        individualImmunityWins: 0,
        votesAgainst: 0,
        idolObtained: 0,
        advantageObtained: 0,
        playedIdolAdvantage: 0,
        advantageAbandoned: 0,
        idolAbandoned: 0,
        shotInDarkWin: 0,
        coinFlipWin: 0,
        finalPlacement: 0,
        votedOut: false,
        involuntaryEvacuation: false,
        voluntaryExit: false,
        notes: ''
      };

      return {
        ...prev,
        [episodeKey]: {
          ...episodeScores,
          [castawayId]: {
            ...castawayScores,
            [field]: value
          }
        }
      };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    if (!selectedLeagueId) {
      setError('No league context found. Please ensure a league is selected in the admin banner.');
      setIsSaving(false);
      return;
    }

    try {
      // Use the league-specific endpoint for saving, matching the pattern in AdminCastawayManagement
      // Logging the URL to the console will help us verify exactly where the request is going
      const url = `${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/scores`;
      console.log('[AdminScoring] Attempting to save scores to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season: adminSeason, scores: allScores })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AdminScoring] Save failed with status ${response.status}:`, errorText);
        throw new Error('Failed to save scores');
      }

      setSuccess('Scores saved successfully!');
    } catch (err) {
      setError('Failed to save scores. Check browser console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleScoreWithGemini = async () => {
    setIsGeminiLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/research-episode-scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          season: adminSeason,
          episode: parseInt(selectedEpisode),
          castaways: activeCastaways.map(c => ({ id: c.id, name: c.name, tribe: c.tribe }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Gemini scoring research failed with status ${response.status}`);
      }

      const data: GeminiScoreResult = await response.json();
      setGeminiResult(data);

      const notesMap: { [castawayId: number]: string } = {};
      const normalize = (n: string) => (n || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      // Apply the scores to allScores for the current episode
      setAllScores((prev: any) => {
        const episodeKey = prev[`episode${selectedEpisode}`] ? `episode${selectedEpisode}` : selectedEpisode;
        const currentEp = { ...(prev[episodeKey] || {}) };

        (data.scores || []).forEach((item: any) => {
          const matchedCastaway = activeCastaways.find(c => {
            const cn = normalize(c.name);
            const iname = normalize(item.name);
            return cn === iname || cn.includes(iname) || iname.includes(cn);
          });

          if (matchedCastaway) {
            notesMap[matchedCastaway.id] = item.notes || '';
            currentEp[matchedCastaway.id] = {
              ...(currentEp[matchedCastaway.id] || {}),
              tribeImmunityWins: Number(item.tribeImmunityWins) || 0,
              tribeRewardWins: Number(item.tribeRewardWins) || 0,
              individualRewardWins: Number(item.individualRewardWins) || 0,
              rightSideOfVote: Number(item.rightSideOfVote) || 0,
              individualImmunityWins: Number(item.individualImmunityWins) || 0,
              votesAgainst: Number(item.votesAgainst) || 0,
              idolObtained: Number(item.idolObtained) || 0,
              advantageObtained: Number(item.advantageObtained) || 0,
              playedIdolAdvantage: Number(item.playedIdolAdvantage) || 0,
              advantageAbandoned: Number(item.advantageAbandoned) || 0,
              idolAbandoned: Number(item.idolAbandoned) || 0,
              shotInDarkWin: Number(item.shotInDarkWin) || 0,
              coinFlipWin: Number(item.coinFlipWin) || 0,
              finalPlacement: Number(item.finalPlacement) || 0,
              votedOut: Boolean(item.votedOut),
              involuntaryEvacuation: Boolean(item.involuntaryEvacuation),
              voluntaryExit: Boolean(item.voluntaryExit),
              notes: item.notes || ''
            };
          }
        });

        return {
          ...prev,
          [episodeKey]: currentEp
        };
      });

      setGeminiNotes(notesMap);
      setSuccess(`✨ Gemini scoring search complete for Season ${adminSeason}, Episode ${selectedEpisode}! The table has been auto-populated with detected events. Please review values before saving.`);
    } catch (err: any) {
      console.error('Gemini scoring research error:', err);
      setError(err.message || 'Failed to research episode scores with Gemini.');
    } finally {
      setIsGeminiLoading(false);
    }
  };

  const selectedLeague = leagues.find((l: any) => String(l.id) === String(selectedLeagueId)) || leagues[0];

  // Support various episode key formats: "1", 1, "episode1"
  const currentEpisodeScores = allScores[selectedEpisode] || 
                               allScores[parseInt(selectedEpisode)] || 
                               allScores[`episode${selectedEpisode}`] || 
                               {};

  // Filter out castaways who were eliminated in a previous episode
  const activeCastaways = castaways.filter(c => {
    const currentEpNum = parseInt(selectedEpisode);
    // Check all episodes before the current one
    for (let i = 1; i < currentEpNum; i++) {
      const prevEpScores = allScores[String(i)] || allScores[i] || allScores[`episode${i}`];
      if (prevEpScores) {
        const s = (prevEpScores as any)[c.id] || 
                  (prevEpScores as any)[String(c.id)] ||
                  (prevEpScores as any)[`castaway${c.id}`] ||
                  (prevEpScores as any)[`c${c.id}`];
        if (s && (s.votedOut || s.involuntaryEvacuation || s.voluntaryExit)) return false;
      }
    }
    return true;
  });

  // Sync geminiNotes from stored score notes whenever selected episode or scores change
  useEffect(() => {
    const episodeKey = allScores[`episode${selectedEpisode}`] ? `episode${selectedEpisode}` : selectedEpisode;
    const epScores = allScores[episodeKey] || allScores[selectedEpisode] || allScores[parseInt(selectedEpisode)] || {};
    const notesMap: { [castawayId: number]: string } = {};

    activeCastaways.forEach(c => {
      const s = (epScores as any)[c.id] || 
                (epScores as any)[String(c.id)] ||
                (epScores as any)[`castaway${c.id}`] ||
                (epScores as any)[`c${c.id}`];
      if (s && s.notes) {
        notesMap[c.id] = s.notes;
      }
    });

    if (Object.keys(notesMap).length > 0) {
      setGeminiNotes(prev => ({ ...prev, ...notesMap }));
    } else {
      setGeminiNotes(notesMap);
    }
  }, [selectedEpisode, allScores, castaways]);

  const handleEpisodeChange = (newEpisode: string) => {
    setSelectedEpisode(newEpisode);
    setGeminiResult(null);
    setError(null);
    setSuccess(null);
  };

  const scoreFields: { label: string, key: keyof EpisodeScore, type: 'number' | 'boolean' }[] = [
    { label: 'Tribe Imm.', key: 'tribeImmunityWins', type: 'number' },
    { label: 'Tribe Rew.', key: 'tribeRewardWins', type: 'number' },
    { label: 'Ind. Rew.', key: 'individualRewardWins', type: 'number' },
    { label: 'Voted Right', key: 'rightSideOfVote', type: 'number' },
    { label: 'Ind. Imm.', key: 'individualImmunityWins', type: 'number' },
    { label: 'Votes Ag.', key: 'votesAgainst', type: 'number' },
    { label: 'Idol Found', key: 'idolObtained', type: 'number' },
    { label: 'Adv. Found', key: 'advantageObtained', type: 'number' },
    { label: 'Played Idol/Adv', key: 'playedIdolAdvantage', type: 'number' },
    { label: 'Adv. Aban.', key: 'advantageAbandoned', type: 'number' },
    { label: 'Idol Aban.', key: 'idolAbandoned', type: 'number' },
    { label: 'SID Win', key: 'shotInDarkWin', type: 'number' },    {label: 'Coin Flip', key: 'coinFlipWin', type: 'number'},    { label: 'Final Place', key: 'finalPlacement', type: 'number' },
    { label: 'Out', key: 'votedOut', type: 'boolean' },
    { label: 'Evac', key: 'involuntaryEvacuation', type: 'boolean' },
    { label: 'Vol. Exit', key: 'voluntaryExit', type: 'boolean' },
  ];

  return (
    <Container className="mt-4 mb-5">
      <h2>Admin Scoring - Working on Season {adminSeason}</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {isLoading ? (
        <div className="text-center mt-5"><Spinner animation="border" /></div>
      ) : leagues.length === 0 ? (
        <Alert variant="info">No leagues found for Season {adminSeason}. You must create a league before adding scores.</Alert>
      ) : (
        <>
          <Row className="mb-4 align-items-end">
            <Col md={4} sm={12} className="mb-2 mb-md-0">
              <Form.Group>
                <Form.Label className="fw-bold">Select Episode</Form.Label>
                <Form.Select value={selectedEpisode} onChange={(e) => handleEpisodeChange(e.target.value)}>
                  {[...Array(15)].map((_, i) => <option key={i+1} value={String(i+1)}>Episode {i+1} {i === 0 ? '(Pre-Draft Tracking)' : ''}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} sm={6} className="mb-2 mb-md-0">
              <Button 
                variant="outline-primary" 
                onClick={handleScoreWithGemini} 
                disabled={isGeminiLoading || isLoading || castaways.length === 0} 
                className="w-100 fw-bold"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {isGeminiLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span>Searching Gemini...</span>
                  </>
                ) : (
                  <>
                    <span>✨ Score with Gemini</span>
                  </>
                )}
              </Button>
            </Col>
            <Col md={4} sm={6}>
              <Button variant="primary" onClick={handleSave} disabled={isSaving || castaways.length === 0} className="w-100 fw-bold">
                {isSaving ? 'Saving...' : 'Save All Scores'}
              </Button>
            </Col>
          </Row>

          {geminiResult && (
            <Card className="mb-4 shadow-sm border-primary">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold" style={{ fontSize: '1rem', letterSpacing: '0.2px' }}>
                  ✨ Gemini Episode Research: {geminiResult.episodeTitle || `Season ${adminSeason} Episode ${selectedEpisode}`}
                </span>
                {geminiResult.votedOutCastaway && (
                  <Badge bg="danger" className="px-3 py-1 fs-6">Voted Out: {geminiResult.votedOutCastaway}</Badge>
                )}
              </Card.Header>
              <Card.Body>
                {geminiResult.episodeSummary && (
                  <p className="mb-2 text-dark" style={{ fontSize: '0.92rem', lineHeight: '1.5' }}>
                    {geminiResult.episodeSummary}
                  </p>
                )}
                {geminiResult.sources && geminiResult.sources.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 align-items-center mt-2 pt-2 border-top" style={{ fontSize: '0.8rem' }}>
                    <span className="text-muted fw-bold">Sources:</span>
                    {geminiResult.sources.slice(0, 4).map((s, idx) => (
                      <a key={idx} href={s.uri} target="_blank" rel="noopener noreferrer" className="badge bg-light text-dark border text-decoration-none">
                        {s.title || s.uri}
                      </a>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {selectedEpisode === '1' && (
            <Alert variant="info" className="mb-3">
              ℹ️ <strong>Episode 1 Pre-Draft Tracking:</strong> Record Idols Found, Advantages Found, or Eliminations from the premiere so contestant inventories and elimination statuses are accurately tracked for Season {adminSeason}. <em>(Note: Episode 1 events do not award fantasy points to player league totals).</em>
            </Alert>
          )}

          {castaways.length === 0 ? (
            <Alert variant="warning">No castaways found for this league.</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Castaway</th>
                    <th>Tribe</th>
                    {scoreFields.map(f => <th key={f.key} style={{ fontSize: '0.8rem', textAlign: 'center' }}>{f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                {activeCastaways.map(c => {
                    // Robust lookup for castaway ID: number, string, or prefixed
                    const scores = (currentEpisodeScores as any)[c.id] || 
                                   (currentEpisodeScores as any)[String(c.id)] ||
                                   (currentEpisodeScores as any)[`castaway${c.id}`] ||
                                   (currentEpisodeScores as any)[`c${c.id}`] || 
                                   currentEpisodeScores[String(c.id)] || 
                                   {};
                                   
                    return (
                      <tr key={c.id}>
                        <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <div>
                            <strong>{c.name}</strong>
                            {geminiNotes[c.id] && (
                              <div style={{ fontSize: '0.72rem', color: '#6c757d', maxWidth: '170px', whiteSpace: 'normal', lineHeight: '1.2' }}>
                                🤖 {geminiNotes[c.id]}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <TribeBadge tribeName={c.tribe} tribes={selectedLeague?.tribes} tribeColor={c.tribeColor} />
                        </td>
                        {scoreFields.map(f => (
                          <td key={f.key} className="text-center" style={{ verticalAlign: 'middle' }}>
                            {f.key === 'individualImmunityWins' ? (
                              <Form.Select 
                                size="sm"
                                value={scores[f.key] ?? 0}
                                onChange={(e) => handleScoreChange(c.id, f.key, parseFloat(e.target.value) || 0)}
                                style={{ width: '120px', margin: '0 auto', fontSize: '0.75rem' }}
                              >
                                <option value="0">None</option>
                                <option value="3">13 Players (3 pts)</option>
                                <option value="3.5">12 Players (3.5 pts)</option>
                                <option value="4">11 Players (4 pts)</option>
                                <option value="4.5">10 Players (4.5 pts)</option>
                                <option value="5">9 Players (5 pts)</option>
                                <option value="5.5">8 Players (5.5 pts)</option>
                                <option value="6">7 Players (6 pts)</option>
                                <option value="6">6 Players (6 pts)</option>
                                <option value="7">5 Players (7 pts)</option>
                                <option value="7.5">4 Players (7.5 pts)</option>
                              </Form.Select>
                            ) : f.type === 'number' ? (
                              <Form.Control 
                                type="number" 
                                size="sm" 
                                step={f.key === 'votesAgainst' ? 0.5 : 1}
                                value={scores[f.key] ?? 0}
                                onChange={(e) => handleScoreChange(c.id, f.key, parseFloat(e.target.value) || 0)}
                                style={{ width: '60px', margin: '0 auto' }}
                              />
                            ) : (
                              <Form.Check 
                                type="checkbox"
                                checked={!!scores[f.key]}
                                onChange={(e) => handleScoreChange(c.id, f.key, e.target.checked)}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminScoring;